import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Textarea,
  VStack,
  Text,
  Select,
  HStack,
  Flex,
  Input,
  useToast,
} from '@chakra-ui/react';
import { RewriteGenre, CustomPrompt } from '../utils/geminiApi';

interface CustomPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (prompt: CustomPrompt) => void;
  editingPrompt?: CustomPrompt | null;
}

const CustomPromptModal: React.FC<CustomPromptModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingPrompt,
}) => {
  const [name, setName] = useState('');
  const [genre, setGenre] = useState<RewriteGenre | 'custom'>('general');
  const [prompt, setPrompt] = useState('');
  const toast = useToast();

  useEffect(() => {
    if (editingPrompt) {
      setName(editingPrompt.name);
      setGenre(editingPrompt.genre);
      setPrompt(editingPrompt.prompt);
    } else {
      setName('');
      setGenre('general');
      setPrompt('');
    }
  }, [editingPrompt, isOpen]);

  const handleSave = () => {
    if (!name.trim()) {
      toast({
        title: 'エラー',
        description: 'プロンプト名を入力してください',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!prompt.trim()) {
      toast({
        title: 'エラー',
        description: 'プロンプト内容を入力してください',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const customPrompt: CustomPrompt = {
      id: editingPrompt?.id || Date.now().toString(),
      name: name.trim(),
      genre,
      prompt: prompt.trim(),
    };

    onSave(customPrompt);
    onClose();
    
    toast({
      title: editingPrompt ? 'プロンプトを更新しました' : 'プロンプトを保存しました',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleClose = () => {
    setName('');
    setGenre('general');
    setPrompt('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {editingPrompt ? 'プロンプトを編集' : 'カスタムプロンプトを作成'}
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <HStack>
              <Text fontWeight="bold" minW="100px">プロンプト名:</Text>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="例: 技術記事用、カジュアル投稿用"
              />
            </HStack>

            <HStack>
              <Text fontWeight="bold" minW="100px">ジャンル:</Text>
              <Select value={genre} onChange={(e) => setGenre(e.target.value as RewriteGenre | 'custom')}>
                <option value="general">一般</option>
                <option value="blog">ブログ記事</option>
                <option value="twitter">X (Twitter)</option>
                <option value="meeting">議事録</option>
                <option value="idea">アイデア整理</option>
                <option value="summary">要約</option>
                <option value="custom">カスタム</option>
              </Select>
            </HStack>

            <VStack align="stretch" spacing={2}>
              <Flex justify="space-between" align="center">
                <Text fontWeight="bold">プロンプト内容:</Text>
                <Text fontSize="xs" color={prompt.length > 2500 ? "red.500" : "gray.500"}>
                  {prompt.length.toLocaleString()} / 3,000
                </Text>
              </Flex>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                maxLength={3000}
                placeholder={`例:
あなたは技術ライターです。
以下の入力内容を技術記事として整理してください。
- コードブロックを使って具体例を示す
- 初心者にも分かりやすい説明を心がける
- 実装のポイントを明確にする`}
                rows={10}
                resize="vertical"
              />
              <Text fontSize="sm" color="gray.500">
                プロンプトには「前置きや挨拶は一切不要です」などの指示を含めることをお勧めします。
              </Text>
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            キャンセル
          </Button>
          <Button colorScheme="blue" onClick={handleSave}>
            {editingPrompt ? '更新' : '保存'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CustomPromptModal;